import React, { useCallback, useEffect, useRef, useState } from "react";
import sortBy from "lodash/sortBy";
import { useQuery } from "@apollo/client/react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Skeleton from "@mui/material/Skeleton";

import { useCustomFields } from "../../../contexts/customFields";
import useAuth from "../../../hooks/useAuth";
import { GET_LOCATIONS_QUERY } from "../../../constants/graphql/queries";
import { onOptionSelected } from "./Address";

const filter = createFilterOptions();

export function filterOptions(options, params) {
  const filtered = filter(options, params);
  const { inputValue } = params;
  const isExisting = options.some(option => inputValue === option.title);
  if (inputValue !== "" && !isExisting) {
    filtered.push({
      inputValue,
      label: inputValue,
    });
  }
  return filtered;
}

const isLocationOptionEqualToValue = (option, value) => {
  if (!value) return false;
  if (typeof value === "string") {
    return option.value === value || option.label === value;
  }
  if (value.inputValue) {
    return option.value === value.inputValue || option.label === value.label;
  }

  return option.value === value.value;
};

export default function InventoryItemAddress({ disabled, error, onChange, inventoryItem }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const wizardMode = settings?.wizard_mode;
  const simpleCheckoutForContractWarehouse = settings?.simple_checkout_for_contract_warehouse;
  const useLocations = wizardMode || simpleCheckoutForContractWarehouse;
  const { data: { locations = [] } = {}, loading } = useQuery(GET_LOCATIONS_QUERY, {
    fetchPolicy: "cache-first",
    skip: !useLocations,
  });
  const [display] = useCustomFields();
  const streetRef = useRef();
  const cityRef = useRef();
  const zipRef = useRef();
  const [selected, setSelected] = useState(inventoryItem.location || "");
  const displayFields = inventoryItem.street || inventoryItem.city || inventoryItem.zip;

  const addressOptions = useLocations
    ? (() => {
        // Clone locations array to avoid mutating frozen Apollo cache objects
        const priorityLocations = [...locations]
          .filter(l => l.favoriteLocation)
          .sort((a, b) => {
            if (a.priority == null && b.priority == null) return a.name.localeCompare(b.name);
            if (a.priority == null) return 1;
            if (b.priority == null) return -1;
            return a.priority - b.priority;
          })
          .map(l => ({
            value: `${l.name}, ${l.address}`,
            label: `${l.name}, ${l.address}`,
            group: "Frequent Locations",
          }));
        const allLocations = [...locations]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(l => ({
            value: `${l.name}, ${l.address}`,
            label: `${l.name}, ${l.address}`,
            group: "All Locations",
          }));
        return [...priorityLocations, ...allLocations];
      })()
    : [];

  const handleChange = useCallback(
    field => e => {
      const item = { ...inventoryItem, [field]: e.target.value };
      const location = [item.street, item.city, item.zip].filter(v => v).join(", ");
      setSelected(location);
      onChange({ location, [field]: e.target.value });
    },
    [inventoryItem, setSelected, onChange]
  );

  useEffect(() => {
    setSelected(inventoryItem.location);
  }, [inventoryItem.location]);

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={40} />
        ) : (
          <Autocomplete
            disabled={disabled}
            size="small"
            freeSolo
            filterOptions={filterOptions}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            groupBy={option => option.group || undefined}
            isOptionEqualToValue={isLocationOptionEqualToValue}
            getOptionLabel={onOptionSelected}
            onChange={(e, value) => {
              const location = value?.inputValue || value?.value || "";
              const [, street = "", city = "", zipCode = ""] = location.split(",");
              const code = zipCode.trim().split(" ");
              setSelected(value);
              onChange({
                street: street.trim(),
                city: city.trim(),
                zip: code[code.length - 1],
                location,
              });
              setTimeout(() => {
                if (streetRef.current) {
                  streetRef.current.value = street.trim();
                }
                if (cityRef.current) {
                  cityRef.current.value = city.trim();
                }
                if (zipRef.current) {
                  zipRef.current.value = code[code.length - 1] || "";
                }
              });
            }}
            options={addressOptions}
            value={selected}
            renderInput={params => (
              <TextField
                {...params}
                error={error}
                label="Delivery Location"
                variant="outlined"
                margin="none"
              />
            )}
          />
        )}
      </Grid>
      {(selected || displayFields) && display.ii_location_address && (
        <>
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              key={inventoryItem.street}
              disabled={disabled}
              inputRef={streetRef}
              fullWidth
              label="Street"
              placeholder="Street"
              defaultValue={inventoryItem.street || ""}
              size="small"
              onBlur={handleChange("street")}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 5 }}>
            <TextField
              key={inventoryItem.city}
              disabled={disabled}
              inputRef={cityRef}
              fullWidth
              label="City"
              placeholder="City"
              defaultValue={inventoryItem.city || ""}
              size="small"
              onBlur={handleChange("city")}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField
              key={inventoryItem.zip}
              disabled={disabled}
              inputRef={zipRef}
              fullWidth
              label="Zip Code"
              placeholder="Zip Code"
              defaultValue={inventoryItem.zip || ""}
              size="small"
              onBlur={handleChange("zip")}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}
